import { G } from '@mobily/ts-belt'
import { useQueryClient } from '@tanstack/vue-query'
import { useMounted } from '@vueuse/core'
import type { AxiosError } from 'axios'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onBeforeUnmount, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { DEFAULT_NUTRIENTS } from '@/const'
import { useSearchDishesAndIngredients } from '@/hooks/useSearchDishesAndIngredients'
import { dietsService } from '@/services/dietsService'
import { useDietsStore } from '@/store/dietsStore'
import { useGlobalStore } from '@/store/globalStore'
import { getDietDaysCount } from '@/utils/dietDays'
import { reportError } from '@/utils/reportError'

import type { DietType } from '@/store/types'
import type { Nutrients } from '@/utils/nutrients'

import { DietDetailInject } from '../../provider'

export function useDietDetail() {
  const queryClient = useQueryClient()
  const dietStore = useDietsStore()
  const route = useRoute()
  const router = useRouter()
  const isMounted = useMounted()
  const dietId = ref(Number(route.params.dietId))
  const dietType = ref(route.params.dietType as DietType)
  const isGeneratingDietDays = ref(false)
  const nutrientValue = ref<'choosen' | Nutrients>('choosen')
  const showAllDays = ref(false)
  const selectedDayIndex = ref(0)
  const { diet } = storeToRefs(dietStore)
  const { resetDietsStore, updateDiet, updateRepeatedDishesAndIngredients, notifyDietRefresh } =
    dietStore

  const nutrients = computed<Nutrients[]>(() => {
    if (
      diet.value &&
      useGlobalStore().hasPerm('accounts.diet_microelements') &&
      nutrientValue.value === 'choosen'
    ) {
      return diet.value.data.choosen_nutrients
    }
    return DEFAULT_NUTRIENTS
  })
  const search = useSearchDishesAndIngredients(nutrients)

  provide(DietDetailInject, {
    nutrients,
    nutrientValue,
    refetchSearchDishesAndIngredients: search.refetch,
    searchDishesAndIngredients: search.data,
    searchDishesAndIngredientsLoading: search.isPending,
    searchDishesAndIngredientsStatus: search.status,
    selectedDayIndex,
    showAllDays,
  })

  watch(
    () => route.params.dayIndex,
    dayIndexParam => {
      if (route.name !== 'DietDetailDay' || dayIndexParam === undefined) return
      const parsedDayIndex = Number.parseInt(dayIndexParam.toString(), 10)
      if (Number.isNaN(parsedDayIndex)) return
      selectedDayIndex.value = Math.min(
        Math.max(parsedDayIndex, 0),
        Math.max(getDietDaysCount(diet.value) - 1, 0),
      )
    },
    { immediate: true },
  )

  watch(
    () => getDietDaysCount(diet.value),
    daysCount => {
      selectedDayIndex.value = Math.min(selectedDayIndex.value, Math.max(daysCount - 1, 0))
    },
  )

  const patientId = computed(() =>
    diet.value && 'dietitian_patient' in diet.value ? diet.value.dietitian_patient.id : undefined,
  )

  async function fetchData(resetState = false) {
    try {
      const response = await dietsService.fetchDiet(dietType.value, Number(route.params.dietId))
      updateDiet({ diet: response.data, dietType: dietType.value, resetState })
      if (dietType.value === 'patient') {
        const repeated = await dietsService.fetchPatientDietRepeatedDishes(
          dietId.value,
          diet.value?.data.repeated_diets_count ?? 5,
        )
        updateRepeatedDishesAndIngredients(repeated.data)
      }
    } catch (error) {
      reportError(error, 'Error while fetching data', {
        id: route.params.dietId,
        type: dietType.value,
      })
      if ((error as AxiosError).response?.status === 404) {
        void router.push({ name: 'PageNotFound' })
      }
    }
  }

  function refreshDietList() {
    void queryClient.invalidateQueries({
      queryKey: ['patientDietList', patientId.value ?? dietId.value],
    })
  }

  async function onDietActionRefresh() {
    await fetchData()
    refreshDietList()
    if (diet.value?.id) notifyDietRefresh(diet.value.id)
  }

  function onSelectActiveDiet(selectedDietId: number) {
    if (selectedDietId === dietId.value) return
    void router.push({
      name: route.name as string,
      params: { ...route.params, dietId: String(selectedDietId) },
    })
  }

  watch(
    () => route.params,
    (newParams, oldParams) => {
      if (
        G.isNotNullable(newParams.dietId) &&
        G.isNotNullable(newParams.dietType) &&
        G.isNotNullable(oldParams) &&
        (newParams.dietId !== oldParams.dietId || newParams.dietType !== oldParams.dietType)
      ) {
        dietId.value = Number(newParams.dietId)
        dietType.value = newParams.dietType as DietType
        void fetchData(true)
      }
    },
  )

  onBeforeMount(fetchData)
  onBeforeUnmount(resetDietsStore)

  return {
    dietId,
    dietType,
    isGeneratingDietDays,
    isMounted,
    onDietActionRefresh,
    onSelectActiveDiet,
    showAllDays,
  }
}
